import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import { useToast } from '../../context/ToastContext';
import { apiClient } from '../../api/client';
import { createJob, updateJob } from '../../api/company';
import { JOB_TYPES } from '../../config/constants';

const schema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().min(20, 'Description is too short'),
  skills: z.string().min(1, 'At least one skill is required'),
  eligibility: z.string().optional(),
  salary: z.string().optional(),
  location: z.string().min(2, 'Location is required'),
  employment_type: z.string().min(1, 'Type is required'),
  deadline: z.string().min(1, 'Deadline is required'),
  openings: z.string().or(z.number()).transform(val => Number(val)).refine(val => val > 0, 'Must be greater than 0'),
});

const PostJob = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(isEditing);
  
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      employment_type: 'Full Time',
      openings: 1
    }
  });

  useEffect(() => {
    if (isEditing) {
      const fetchJob = async () => {
        try {
          const res = await apiClient.get(`/opportunities/${id}`);
          const job = res.data;
          
          // Format date for input[type="date"]
          let formattedDate = '';
          if (job.deadline) {
            formattedDate = new Date(job.deadline).toISOString().split('T')[0];
          }
          
          reset({
            ...job,
            deadline: formattedDate
          });
        } catch (err) {
          addToast('Failed to load job details', 'error');
          navigate('/company/jobs');
        } finally {
          setLoading(false);
        }
      };
      fetchJob();
    }
  }, [id, isEditing, reset, addToast, navigate]);

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await updateJob(id, data);
        addToast('Job updated successfully', 'success');
      } else {
        await createJob(data);
        addToast('Job posted successfully', 'success');
      }
      navigate('/company/jobs');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to save job', 'error');
    }
  };

  if (loading) return <div className="page-wrapper"><Navbar /><Loader fullPage /><Footer /></div>;

  return (
    <div className="page-wrapper bg-gray-50">
      <Navbar />
      
      <div className="container py-8 max-w-4xl">
        <div className="page-back-bar !bg-transparent !border-none !p-0 mb-6">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900 flex items-center gap-2 text-sm font-medium">
            ← Back
          </button>
        </div>
        
        <div className="card-elevated">
          <div className="border-b border-gray-100 pb-6 mb-6">
            <h1 className="heading-2">{isEditing ? 'Edit Job Posting' : 'Post a New Job'}</h1>
            <p className="text-gray-500 mt-1">Fill out the details to attract the best talent.</p>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-row">
              <Input
                label="Job Title"
                placeholder="e.g. Software Engineer Intern"
                {...register('title')}
                error={errors.title?.message}
                required
              />
              <div className="form-group">
                <label className="form-label">Employment Type <span className="required">*</span></label>
                <select className="form-input select" {...register('employment_type')}>
                  {JOB_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
                {errors.employment_type && <span className="form-error">{errors.employment_type.message}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Job Description <span className="required">*</span></label>
              <textarea 
                className={`form-input textarea ${errors.description ? 'error' : ''}`}
                style={{ minHeight: '150px' }}
                placeholder="Describe the role, responsibilities, and what you're looking for..."
                {...register('description')}
              ></textarea>
              {errors.description && <span className="form-error">{errors.description.message}</span>}
            </div>
            
            <div className="form-row">
              <Input
                label="Required Skills"
                placeholder="React, Python, SQL (comma separated)"
                {...register('skills')}
                error={errors.skills?.message}
                required
              />
              <Input
                label="Location"
                placeholder="e.g. New York, NY or Remote"
                {...register('location')}
                error={errors.location?.message}
                required
              />
            </div>
            
            <div className="form-row">
              <Input
                label="Salary / Stipend"
                placeholder="e.g. $50k-$70k or $1000/mo"
                {...register('salary')}
                error={errors.salary?.message}
              />
              <Input
                label="Eligibility Criteria"
                placeholder="e.g. B.Tech CS 2024 Batch, > 8.0 CGPA"
                {...register('eligibility')}
                error={errors.eligibility?.message}
              />
            </div>
            
            <div className="form-row">
              <Input
                label="Application Deadline"
                type="date"
                {...register('deadline')}
                error={errors.deadline?.message}
                required
              />
              <Input
                label="Number of Openings"
                type="number"
                min="1"
                {...register('openings')}
                error={errors.openings?.message}
                required
              />
            </div>
            
            <div className="border-t border-gray-100 pt-6 mt-6 flex justify-end gap-4">
              <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" size="lg" isLoading={isSubmitting}>
                {isEditing ? 'Save Changes' : 'Post Job'}
              </Button>
            </div>
          </form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PostJob;